import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Smartphone, Apple, Bot, QrCode } from "lucide-react";

const TARGETS = ["web", "ios", "android"];

export default function Mobile() {
  const { org } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [targets, setTargets] = useState(["web"]);
  const [expo, setExpo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!org?.id) return;
    api.get(`/orgs/${org.id}/projects`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
      setProjects(list);
      if (list[0]) setPid(list[0].id);
    }).catch((e) => toast.error(errMsg(e)));
  }, [org]);

  useEffect(() => {
    if (!pid) return;
    setExpo(null); setPreview(null);
    api.get(`/mobile/${pid}/expo-config`).then((r) => setExpo(r.data)).catch(() => {});
    api.get(`/mobile/${pid}/preview`).then((r) => setPreview(r.data)).catch(() => {});
  }, [pid]);

  const toggle = (t) => setTargets((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/mobile/targets", { project_id: pid, targets });
      toast.success("Build targets saved");
      setTargets(data.build_targets || targets);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Shell>
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }}>Mobile</h1>

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="mobile-project-picker">
        <label className="k-muted" style={{ fontSize: 12 }}>Project</label>
        <select className="k-control" data-testid="mobile-project-select" value={pid} onChange={(e) => setPid(e.target.value)} style={{ marginTop: 4 }}>
          {projects.length === 0 && <option value="">No projects yet</option>}
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <div style={{ marginTop: 14 }}>
          <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>Build targets</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TARGETS.map((t) => (
              <button key={t} type="button" data-testid={`mobile-target-${t}`} onClick={() => toggle(t)}
                className={targets.includes(t) ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"}
                style={{ textTransform: "uppercase", fontSize: 12 }}>
                {t === "ios" ? <Apple size={14} /> : t === "android" ? <Bot size={14} /> : <Smartphone size={14} />} {t}
              </button>
            ))}
          </div>
          <button className="k-btn k-btn-primary" data-testid="mobile-save-targets" data-loading={saving} disabled={!pid || saving}
            onClick={save} style={{ marginTop: 12 }}>Save targets</button>
        </div>
      </div>

      {expo && (
        <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="mobile-expo-config">
          <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}>{expo.framework}</h3>
          <p className="k-muted" style={{ fontSize: 12 }}>App: {expo.app_config?.name} · scheme {expo.app_config?.scheme}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {(expo.native_features || []).map((f) => <span key={f} className="k-badge k-badge-sandbox">{f}</span>)}
          </div>
          <p className="k-muted" style={{ fontSize: 11.5 }}>{expo.ownership}</p>
          {(expo.apple_notes || []).map((n, i) => <p key={i} className="k-muted" style={{ fontSize: 11.5, margin: "2px 0" }}>Apple — {n}</p>)}
          <p className="k-muted" style={{ fontSize: 11.5 }}>Google Play data-safety: {(expo.google_play?.data_safety_form?.collected_data_categories || []).join(", ") || "—"} <span className="k-badge">{expo.google_play?.data_safety_form?.label}</span></p>
        </div>
      )}

      {preview && (
        <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="mobile-preview">
          <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}><QrCode size={15} /> Multi-device preview</h3>
          <p className="k-muted" style={{ fontSize: 12 }}>{preview.note}</p>
          {preview.frames?.expo_qr?.sandbox && <span className="k-badge k-badge-sandbox" data-testid="mobile-expo-sandbox">SANDBOX · Expo QR live-test bridge</span>}
        </div>
      )}
    </Shell>
  );
}
